import React, {useEffect, useMemo, useState} from "react";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    Input,
    Text,
    useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../constants/routes-path";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { checkErrors, hasError } from "../../../utils/forms-utils";
import { processesService } from "../../../services/processesService";
import { validateEmail } from "../../../utils";
import Loading from "../../../components/Loading/Loading";

function SendMail({data}) {
    const textColor = useColorModeValue("gray.700", "white");
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const { id } = useParams();
    const [processData, setProcessData] = useState(null);
    const [loading, setLoading] = useState(false);

    const [inputError, setInputError] = useState({});

    const ids = useMemo(() => ({
        user: "user",
        email: "email",
        file: "file",
        processId: "processId",
    }), []);

    const [formData, setFormData] = useState( {
        user: "",
        file: "",
        processId: "",
        email: {
            value: "",
            validations: [
                {required: true},
                {
                    callback: ({value}) =>
                        value && !validateEmail(value)
                            ? "settings.person.form.invalid-email"
                            : null,
                }
            ]
        }
    });

    const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

    const handleInputChange = (e) => {
        const { id, type } = e.target;
        const value = type === "checkbox" ? e.target.checked : e.target.value;

        const newFormData = {
            ...formData,
            [id]: {
                ...formData[id],
                value: value
            }
        };
        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
    };

    useEffect(() => {
        if (id) {
            fetchProcess();
        }
    }, [id]);

    const fetchProcess = async () => {
        setLoading(true);
        try {
            const process = await processesService.find(id);
            setProcessData(process);
            setLoading(false);
        } catch (error) {
            showToast({ title: t("process.messages.errors.find"), status: "error" });
            setLoading(false);
            redirectToList();
        }
    }

    const redirectToList = () => {
        navigate(ROUTES.processesPath);
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        if (isFormInvalid) return;
        const form = new FormData();
        form.append('email', formData[ids.email].value);
        form.append('processId', id);

        processesService.sendMail(id, form)
            .then(() => {
                setLoading(false);
                showToast({ title: t("system.notifications.success"), status: "success" });
                redirectToList();
            })
            .catch(() => {
                setLoading(false);
                showToast({
                    title: t("system.notifications.error"),
                    description: error.message,
                    status: "error"
                });
            });
    };


    return (
        <Flex direction="column" minH="89vh" align="center" pt={{sm: "120px", md: "75px"}}>
            <Card mt={"80px"}>
                <CardHeader mb="40px">
                    <Flex direction="column">
                        <Text color={textColor} fontSize="lg" fontWeight="bold" mb="3px">
                            {t("process.send-mail.form.title")}
                        </Text>
                        <Text color="gray.400" fontWeight="normal" fontSize="sm">
                            {processData && (
                                <>
                                    {t("process.send-mail.form.subtitle")} {processData.numero}
                                </>
                            )}
                        </Text>
                    </Flex>
                </CardHeader>
                <CardBody>
                    {loading ? (<Loading h="300px" w="100%" />) : (
                        <form onSubmit={handleSubmit}>
                            <Flex direction="column" w="100%">
                                <Grid>
                                    {/*email*/}
                                    <FormControl isRequired isInvalid={!!inputError[ids.email]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`process.form.fields.requester-email.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.email}
                                            value={formData[ids.email].value}
                                            onChange={handleInputChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.email])}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Grid>
                                <Flex justify="space-between">
                                    <Button
                                        variant="light"
                                        fontSize="sm"
                                        alignSelf="flex-end"
                                        mt="24px"
                                        w="100px"
                                        h="35px"
                                        onClick={redirectToList}
                                    >
                                        {t("system.actions.back")}
                                    </Button>
                                    <Button
                                        variant="dark"
                                        fontSize="sm"
                                        alignSelf="flex-end"
                                        type="submit"
                                        mt="24px"
                                        w="100px"
                                        h="35px"
                                        isDisabled={isFormInvalid}
                                        _hover={isFormInvalid ? {} : undefined}
                                    >
                                        {t("system.actions.save")}
                                    </Button>
                                </Flex>
                            </Flex>
                        </form>
                    )}
                </CardBody>
            </Card>
        </Flex>
    );
}

export default SendMail
